import axios from 'axios';

export const MAX_ATTACHMENT_SIZE_BYTES = 25 * 1024 * 1024;

export const ATTACHMENT_ACCEPT = [
  '.pdf', '.ppt', '.pptx', '.doc', '.docx', '.xls', '.xlsx',
  '.png', '.jpg', '.jpeg', '.webp',
].join(',');

const ALLOWED_ATTACHMENT_EXTENSIONS = new Set([
  'pdf', 'ppt', 'pptx', 'doc', 'docx', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 'webp',
]);

const ATTACHMENT_MIME_BY_EXTENSION = Object.freeze({
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
});

const getFileExtension = (file) => (
  file?.name?.includes('.') ? file.name.split('.').pop().toLowerCase() : ''
);

const getFileMimeType = (file) => {
  const extension = getFileExtension(file);
  const knownMimeType = ATTACHMENT_MIME_BY_EXTENSION[extension];
  return knownMimeType || file?.type || 'application/octet-stream';
};

export const validateAttachmentFile = (file) => {
  if (!file) return 'No file selected';
  if (file.size <= 0) return 'Empty files cannot be uploaded';
  if (file.size > MAX_ATTACHMENT_SIZE_BYTES) return 'File must be 25 MiB or smaller';
  const extension = getFileExtension(file);
  if (!ALLOWED_ATTACHMENT_EXTENSIONS.has(extension)) return 'File type is not supported';
  return null;
};

export const formatFileSize = (size) => {
  const bytes = Number(size || 0);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const listTaskAttachments = async (api, taskId) => {
  const response = await api.get(`/tasks/${taskId}/attachments`);
  const data = response.data;
  if (Array.isArray(data)) return { attachments: data, permissions: {} };
  return {
    attachments: Array.isArray(data?.attachments) ? data.attachments : [],
    permissions: data?.permissions || {},
  };
};

export const createAttachmentUploadIntent = async (api, taskId, file) => {
  const response = await api.post(`/tasks/${taskId}/attachments/upload-intents`, {
    original_name: file.name,
    mime_type: getFileMimeType(file),
    size_bytes: file.size,
  });
  return response.data;
};

const resolveIntentParts = (intent) => {
  const attachment = intent?.attachment || intent;
  const upload = intent?.upload || intent;
  return {
    attachment,
    attachmentId: attachment?.id || intent?.attachment_id || intent?.id,
    uploadUrl: upload?.url || intent?.upload_url,
    fields: upload?.fields || intent?.fields || {},
  };
};

export const uploadToPresignedPost = async (intent, file, options = {}) => {
  const { uploadUrl, fields } = resolveIntentParts(intent);
  if (!uploadUrl) throw new Error('Upload URL was not provided');

  const body = new FormData();
  Object.entries(fields || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null) body.append(key, String(value));
  });
  body.append('file', file);

  // Deliberately use a bare Axios request. The authenticated API instance carries
  // the application bearer token, which must never be forwarded to S3.
  await axios.post(uploadUrl, body, {
    signal: options.signal,
    onUploadProgress: options.onUploadProgress,
  });
};

export const completeAttachmentUpload = async (api, taskId, attachmentId) => {
  const response = await api.post(`/tasks/${taskId}/attachments/${attachmentId}/complete`);
  return response.data;
};

export const uploadTaskAttachment = async ({ api, taskId, file, signal, onProgress }) => {
  const validationError = validateAttachmentFile(file);
  if (validationError) throw new Error(validationError);

  onProgress?.(2);
  const intent = await createAttachmentUploadIntent(api, taskId, file);
  const { attachmentId } = resolveIntentParts(intent);
  if (!attachmentId) throw new Error('Attachment ID was not provided');

  onProgress?.(5);
  await uploadToPresignedPost(intent, file, {
    signal,
    onUploadProgress: (event) => {
      if (!event.total) return;
      onProgress?.(5 + Math.round((event.loaded / event.total) * 90));
    },
  });
  onProgress?.(97);
  const attachment = await completeAttachmentUpload(api, taskId, attachmentId);
  onProgress?.(100);
  return attachment;
};

export const requestAttachmentAccess = async (api, attachmentId, disposition) => {
  const response = await api.post(`/attachments/${attachmentId}/access-url`, { disposition });
  return response.data;
};

export const deleteAttachment = async (api, attachmentId) => {
  await api.delete(`/attachments/${attachmentId}`);
};

export const getAttachmentName = (attachment) => (
  attachment?.original_name || attachment?.file_name || attachment?.name || 'Attachment'
);

export const getAttachmentMimeType = (attachment) => (
  attachment?.mime_type || attachment?.content_type || 'application/octet-stream'
);

export const getAttachmentSize = (attachment) => (
  attachment?.size_bytes ?? attachment?.size ?? 0
);

export const isSafeInlinePreview = (attachment) => {
  if (attachment?.previewable === false) return false;
  const mime = getAttachmentMimeType(attachment).toLowerCase();
  return mime === 'application/pdf' || (mime.startsWith('image/') && mime !== 'image/svg+xml');
};

export const openAccessUrl = (access, attachment, disposition) => {
  const url = access?.url || access?.access_url;
  if (!url) throw new Error('File access URL was not provided');

  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  if (disposition === 'attachment') link.download = getAttachmentName(attachment);
  document.body.appendChild(link);
  link.click();
  link.remove();
};
